import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function CalendarFeed({ token, onReset, disabled }) {
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/calendar/${token}.ics`;
  const webcal = url.replace(/^https?:/, "webcal:");
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success("Calendar link copied"); } catch { toast.error("Couldn't copy — select the link and copy it"); } };
  return (
    <div className="rounded-lg border border-slate-200 p-4 space-y-2" data-testid="calendar-feed">
      <Label>Add viewings to your own calendar</Label>
      <p className="text-xs text-slate-500">Subscribe once and confirmed and requested viewings appear in Google, Outlook or Apple Calendar automatically. Keep this link private.</p>
      <div className="flex gap-2"><Input readOnly value={url} className="text-xs" data-testid="calendar-feed-url" onFocus={(e) => e.target.select()} />
        <Button type="button" variant="outline" size="icon" onClick={copy} data-testid="calendar-feed-copy"><Copy className="w-4 h-4" /></Button></div>
      <div className="flex flex-wrap gap-3 text-xs">
        <a className="text-emerald-700 hover:underline" target="_blank" rel="noreferrer" href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcal)}`} data-testid="calendar-feed-google">Add to Google Calendar</a>
        <a className="text-emerald-700 hover:underline" href={webcal} data-testid="calendar-feed-apple">Open in Apple / Outlook</a>
        {!disabled && <button type="button" onClick={onReset} className="text-slate-500 hover:text-slate-900 flex items-center gap-1 ml-auto" data-testid="calendar-feed-reset"><RefreshCw className="w-3 h-3" />Reset link</button>}
      </div>
    </div>
  );
}

function DayRow({ i, h, set, disabled }) {
  return (
    <div className="flex items-center gap-3 text-sm" data-testid={`hours-row-${i}`}>
      <Switch checked={h.on} onCheckedChange={(c) => set({ ...h, on: c })} disabled={disabled} data-testid={`hours-on-${i}`} />
      <span className="w-24">{DAYS[i]}</span>
      {h.on ? <>
        <Input type="time" className="w-28 h-8" value={h.start} onChange={(e) => set({ ...h, start: e.target.value })} disabled={disabled} data-testid={`hours-start-${i}`} />
        <span className="text-slate-400">to</span>
        <Input type="time" className="w-28 h-8" value={h.end} onChange={(e) => set({ ...h, end: e.target.value })} disabled={disabled} data-testid={`hours-end-${i}`} />
      </> : <span className="text-slate-400">Unavailable</span>}
    </div>
  );
}

function Reminders({ s, setS, disabled }) {
  return (
    <div className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid="reminder-settings">
      <div className="flex items-start gap-3">
        <Switch checked={s.reminders} onCheckedChange={(c) => setS({ ...s, reminders: c })} disabled={disabled} data-testid="reminders-switch" />
        <div><Label>Reminder emails before confirmed viewings</Label><p className="text-xs text-slate-500 mt-0.5">The visitor gets a reminder with a calendar file. Checked every hour.</p></div>
      </div>
      {s.reminders && <div className="flex flex-wrap items-center gap-2 text-sm pl-12">
        <span>Send</span>
        <Input type="number" min={1} max={72} className="w-20 h-8" value={s.reminder_hours} onChange={(e) => setS({ ...s, reminder_hours: e.target.value })} disabled={disabled} data-testid="reminder-hours" />
        <span>hours before the viewing</span>
        <label className="flex items-center gap-2 w-full mt-1"><Switch checked={s.remind_agent} onCheckedChange={(c) => setS({ ...s, remind_agent: c })} disabled={disabled} data-testid="remind-agent-switch" />Also remind me (business email)</label>
      </div>}
    </div>
  );
}

export function ViewingSettingsDialog({ open, onOpenChange, disabled }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { api.get("/biz/viewing-settings").then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e))); }
  }, [open]);
  const save = async () => {
    setBusy(true);
    try {
      const { calendar_token, ...body } = s;
      const hrs = Number(body.reminder_hours);
      if (body.reminders && (!Number.isInteger(hrs) || hrs < 1 || hrs > 72)) { toast.error("Reminder hours must be a whole number from 1 to 72"); return; }
      const r = await api.put("/biz/viewing-settings", { ...body, notice_hours: Number(body.notice_hours), days_ahead: Number(body.days_ahead), reminder_hours: Number.isInteger(hrs) && hrs >= 1 && hrs <= 72 ? hrs : 24 });
      setS(r.data); toast.success("Viewing settings saved"); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const reset = async () => { try { const r = await api.post("/biz/viewing-settings/calendar-token"); setS(r.data); toast.success("New calendar link created — the old one no longer works"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="viewing-settings-dialog">
        <DialogHeader><DialogTitle>Viewing settings</DialogTitle><DialogDescription>Choose when people can request viewings from your property pages. You approve every request.</DialogDescription></DialogHeader>
        {!s ? <div className="py-8 text-center text-sm text-slate-500">Loading…</div> : (
          <div className="space-y-5">
            <div className="flex items-start gap-3 rounded-lg border border-slate-200 p-4">
              <Switch checked={s.online_booking} onCheckedChange={(c) => setS({ ...s, online_booking: c })} disabled={disabled} data-testid="online-booking-switch" />
              <div><Label>Online viewing requests</Label><p className="text-xs text-slate-500 mt-0.5">When off, property pages only show the enquiry form. Your calendar still works for appointments you add yourself.</p></div>
            </div>
            <div className={s.online_booking ? "space-y-5" : "space-y-5 opacity-50 pointer-events-none"}>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>Viewing length</Label>
                  <Select value={String(s.slot_minutes)} onValueChange={(v) => setS({ ...s, slot_minutes: Number(v) })} disabled={disabled}>
                    <SelectTrigger data-testid="slot-minutes"><SelectValue /></SelectTrigger>
                    <SelectContent>{[15, 20, 30, 45, 60, 90].map((m) => <SelectItem key={m} value={String(m)}>{m} minutes</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-1.5"><Label>Minimum notice (hours)</Label><Input type="number" min={0} max={168} value={s.notice_hours} onChange={(e) => setS({ ...s, notice_hours: e.target.value })} disabled={disabled} data-testid="notice-hours" /></div>
                <div className="space-y-1.5"><Label>Bookable days ahead</Label><Input type="number" min={1} max={60} value={s.days_ahead} onChange={(e) => setS({ ...s, days_ahead: e.target.value })} disabled={disabled} data-testid="days-ahead" /></div>
              </div>
              <div className="space-y-2"><Label>Weekly availability (UK time)</Label>
                {s.hours.map((h, i) => <DayRow key={i} i={i} h={h} disabled={disabled} set={(nh) => setS({ ...s, hours: s.hours.map((x, j) => (j === i ? nh : x)) })} />)}</div>
            </div>
            <Reminders s={s} setS={setS} disabled={disabled} />
            <CalendarFeed token={s.calendar_token} onReset={reset} disabled={disabled} />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="viewing-settings-cancel">Close</Button>
          {!disabled && <Button onClick={save} disabled={!s || busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="viewing-settings-save">Save settings</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
